import React, { useState } from "react";
import { MessageSquare, MoreHorizontal, Pencil, Trash2, Search, SquarePen } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useLang } from "@/i18n";

const fmtDate = (iso, lang) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "short" });
};

export const RecentAnalysesPanel = ({ convs, activeId, onOpen, onRename, onDelete, onNew }) => {
  const { tf, lang } = useLang();
  const [searchOpen, setSearchOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const list = filter.trim()
    ? convs.filter((c) => (c.title || "").toLowerCase().includes(filter.toLowerCase()))
    : convs;

  return (
    <div data-testid="intel-history-panel">
      <div className="flex items-center gap-1.5">
        <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground flex-1">{tf("Analyses récentes")}</h3>
        <button
          onClick={() => { setSearchOpen((o) => !o); setFilter(""); }}
          aria-label={tf("Rechercher une analyse…")}
          aria-expanded={searchOpen}
          data-testid="intel-history-search-toggle"
          className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
        >
          <Search className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onNew}
          aria-label={tf("Nouvelle analyse")}
          data-testid="intel-history-new"
          className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
        >
          <SquarePen className="h-3.5 w-3.5" />
        </button>
      </div>
      {searchOpen && (
        <input
          autoFocus
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={tf("Rechercher une analyse…")}
          data-testid="intel-history-search-input"
          className="mt-2 w-full h-8 rounded-md border border-border bg-transparent px-2.5 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      )}
      <div className="mt-3 space-y-0.5">
        {list.length === 0 && (
          <p className="text-xs text-muted-foreground py-6 text-center">
            {filter ? tf("Aucune analyse ne correspond.") : tf("Aucune analyse pour le moment.")}
          </p>
        )}
        {list.map((c) => (
          <div
            key={c.id}
            data-testid={`intel-conv-${c.id}`}
            className={cn(
              "group flex items-start gap-2 rounded-lg px-2 py-2 transition-colors",
              activeId === c.id ? "bg-secondary/70" : "hover:bg-secondary/40"
            )}
          >
            <MessageSquare className="h-3.5 w-3.5 text-muted-foreground/60 mt-0.5 shrink-0" aria-hidden="true" />
            <button onClick={() => onOpen(c.id)} className="min-w-0 flex-1 text-left focus-visible:outline-none" data-testid={`intel-conv-open-${c.id}`}>
              <p className="text-xs font-medium leading-snug line-clamp-2">{c.title}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(c.updated_at, lang)}</p>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label={c.title}
                  data-testid={`intel-conv-menu-${c.id}`}
                  className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 p-1 rounded text-muted-foreground hover:text-foreground transition-opacity shrink-0"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[140px]">
                <DropdownMenuItem onClick={() => onRename(c)} className="text-xs gap-2" data-testid={`intel-conv-rename-${c.id}`}>
                  <Pencil className="h-3 w-3" /> {tf("Renommer")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete(c)} className="text-xs gap-2 text-danger focus:text-danger" data-testid={`intel-conv-delete-${c.id}`}>
                  <Trash2 className="h-3 w-3" /> {tf("Supprimer")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>
    </div>
  );
};
